// ─────────────────────────────────────────────────────────────────────────────
// Phase 10 — Canonical Identity (HOOK).
//
// React entry point for the canonical identity layer. Wraps `useAuth()` and
// `useFamilyGraph()` and runs the pure `deriveCanonicalIdentity()` derivation
// on every render so consumers see a consistent, memoized snapshot.
//
// USAGE — every personalized surface in the app should READ from this hook
// instead of independently combining `useAuth().plan` + `useFamilyGraph().
// viewerMemberId`. See IDENTITY_ARCHITECTURE.md §3 (source-of-truth mapping)
// for the full list of which question goes through this hook.
//
//     const id = useCanonicalIdentity();
//     if (id.bindingState === 'bound') {
//       const label = kinshipLabel(target, id.canonicalMemberId, members);
//       …
//     }
//
// HARD GUARANTEES:
//   • Reads only — never mutates auth, profile, or graph state directly.
//   • The single mutator exposed (`bindToMember`) routes through the
//     existing `claimProfile()` pipeline in FamilyGraphContext, which
//     enforces all DB-level safety guards.
//   • Returns a stable object identity across renders when the snapshot
//     is unchanged (so children memoized on the snapshot don't re-render).
// ─────────────────────────────────────────────────────────────────────────────

import { useMemo, useCallback } from 'react';

import { useAuth } from '@/contexts/AuthContext';
import { useFamilyGraph } from '@/contexts/FamilyGraphContext';

import {
  deriveCanonicalIdentity,
  type CanonicalIdentitySnapshot,
} from '@/lib/identity/canonicalIdentity';

export interface UseCanonicalIdentityResult extends CanonicalIdentitySnapshot {
  /**
   * Explicit "this row is me" binding. Wraps the family-graph layer's
   * `bindViewerToMember` which itself wraps `claimProfile`. After it
   * resolves, the next render of this hook returns
   * `bindingState === 'bound'` and `canonicalMemberId === memberId`.
   *
   * Throws the same errors as `claimProfile()` (already-claimed by another
   * user, network failure, etc.). The IdentityBindingModal is responsible
   * for surfacing those errors to the user.
   */
  bindToMember: (memberId: string) => Promise<void>;
  /** Trigger a fresh family-graph reload (useful right after an external write). */
  refresh: () => Promise<void>;
}

export function useCanonicalIdentity(): UseCanonicalIdentityResult {
  const auth = useAuth();
  const graph = useFamilyGraph();

  // The hook is intentionally a thin wrapper — all real logic lives in the
  // pure derivation. This makes it trivially testable and prevents drift.
  const snapshot = useMemo<CanonicalIdentitySnapshot>(
    () =>
      deriveCanonicalIdentity({
        authLoading: auth.loading,
        authUserId: auth.user?.id ?? null,
        authEmail: auth.user?.email ?? auth.memberEmail ?? null,
        authDisplayName:
          auth.memberName ??
          (auth.user?.user_metadata as any)?.name ??
          (auth.user?.user_metadata as any)?.full_name ??
          null,
        profileError: auth.profileError,
        profileLoading: auth.profileLoading,
        plan: auth.plan,
        role: auth.role,
        isPaid: auth.isPaid,
        isStaff: auth.isStaff,
        invitedBy: (auth.profile as any)?.invited_by ?? null,
        graphLoading: graph.loading,
        members: graph.members,
        effectiveOwnerUserId: graph.effectiveOwnerUserId,
        viewerMemberIdFromGraph: graph.viewerMemberId,
        viewerCandidatesFromGraph: graph.viewerCandidates,
      }),
    [
      auth.loading,
      auth.user?.id,
      auth.user?.email,
      auth.memberEmail,
      auth.memberName,
      auth.user?.user_metadata,
      auth.profileError,
      auth.profileLoading,
      auth.plan,
      auth.role,
      auth.isPaid,
      auth.isStaff,
      auth.profile,
      graph.loading,
      graph.members,
      graph.effectiveOwnerUserId,
      graph.viewerMemberId,
      graph.viewerCandidates,
    ],
  );

  const bindToMember = useCallback(
    async (memberId: string): Promise<void> => {
      // Delegate to the existing FamilyGraphContext binding API. That call
      // wraps `claimProfile()` which enforces the DB-level "one claim per
      // user" + "never overwrite another user's claim" guards.
      await graph.bindViewerToMember(memberId);
    },
    [graph],
  );

  const refresh = useCallback(async () => {
    await graph.refresh();
  }, [graph]);

  return useMemo<UseCanonicalIdentityResult>(
    () => ({
      ...snapshot,
      bindToMember,
      refresh,
    }),
    [snapshot, bindToMember, refresh],
  );
}
